import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useQueryGetTirePresetsList } from "src/queries/administrator/tirePreset.queries";
import type { TirePresetRecord } from "src/services/administrator/tirePresetService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import TirePresetsTable from "./TirePresetsTable";
import "../../Customers/Customers.css";
import "./TirePresets.css";

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

interface TirePresetSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const TirePresets = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<TirePresetSorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });
  const [search, setSearch] = useState("");
  const canEdit = hasAdministrationPermission("edit");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sortDir ?? "DESC",
      sortBy: sorting.sortBy ?? "ID",
    }),
    [pagination.page, pagination.size, sorting.sortBy, sorting.sortDir]
  );

  const { data, isLoading } = useQueryGetTirePresetsList(queryParams, {
    enabled: true,
  });

  const response = data?.data?.response;
  const records = useMemo(
    () =>
      response?.resources ??
      response?.tirePresets ??
      response?.data ??
      response?.items ??
      response?.content ??
      [],
    [response]
  );

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) =>
      [record.name, record.configuration, record.active ? "active" : "inactive"]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : (response?.totalItems ?? response?.totalElements ?? response?.total ?? records.length);

  const openCreateForm = () => {
    navigate("/administration/setups/tire-preset-config");
  };

  const openEditForm = (record: TirePresetRecord) => {
    navigate(`/administration/setups/tire-preset-config-edit?id=${encodeURIComponent(record.id)}`);
  };

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="tire_preset"
        buttonKey="create"
        onClick={openCreateForm}
        showBtn={canEdit}
        buttonProps={{
          className: "!text-gray2 !w-[94px] !h-[40px]",
        }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <TirePresetsTable
          data={filteredRecords}
          isLoading={isLoading}
          pagination={{ ...pagination, total: totalCount }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          onEdit={openEditForm}
        />
      </div>
    </section>
  );
};

export default TirePresets;
